import { FC, useMemo } from "react";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { Box } from "@mui/material";
import { makeSxStyles } from "ui";
import { AdminRegionLocationResDto } from "service/api/estabrookSchemas";
import RegionAddressesDroppableContainer from "./region-addresses-droppable-container";
import { ClickedAddress } from "../../../shared/hooks/use-address-click-handler";
import RegionDriverStartingPointDetails from "./region-driver-starting-point-details";

interface RegionAddressesLayoutProps {
  color: string;
  regionId: string;
  locations: AdminRegionLocationResDto[];
  onClickedAddress: (value: ClickedAddress) => void;
  handleMouseEnter: (orderId: string, event: React.MouseEvent) => void;
  handleMouseLeave: () => void;
  driverFullName?: string;
  driverId?: string;
  driverStartingPointAddress?: string;
  showStartingPoint?: boolean;
  isPlanned?: boolean;
}

export const RegionAddressesLayout: FC<RegionAddressesLayoutProps> = ({
  color,
  regionId,
  locations,
  onClickedAddress,
  handleMouseEnter,
  handleMouseLeave,
  driverFullName,
  driverId,
  driverStartingPointAddress,
  showStartingPoint = false,
  isPlanned = false,
}) => {
  const styles = useStyles();

  const allItems = useMemo(() => {
    return locations.flat().map((item) => item.locationId);
  }, [locations]);

  const dropZoneConfig = {
    id: regionId,
    color: color,
    items: locations,
  };

  return (
    <SortableContext items={allItems} strategy={rectSortingStrategy}>
      <Box sx={styles.orderLayoutWrapper}>
        <Box
          sx={{
            ...styles.container,
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }} key={regionId}>
            {showStartingPoint && (
              <RegionDriverStartingPointDetails
                driverFullName={driverFullName}
                driverId={driverId}
                driverStartingPointAddress={driverStartingPointAddress}
              />
            )}
            <RegionAddressesDroppableContainer
              dropZoneDetails={dropZoneConfig}
              handleMouseEnter={handleMouseEnter}
              onClickedAddress={onClickedAddress}
              handleMouseLeave={handleMouseLeave}
              isLastItem={true}
              isPlanned={isPlanned}
            />
          </Box>
        </Box>
      </Box>
    </SortableContext>
  );
};

const useStyles = makeSxStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    minWidth: "100px",
    position: "relative",
    "& > :first-of-type": {
      flexShrink: 0,
    },
    "& > :nth-of-type(2)": {
      flexGrow: 1,
    },
  },
  orderLayoutWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  skippedOrdersContainer: {
    position: "absolute",
    right: "-130px",
    minWidth: "110px",
    "& > :first-of-type": {
      minWidth: "110px",
    },
  },
  deleteButton: {
    display: "none",
    marginLeft: "auto",
    width: "32px",
    height: "32px",
  },
}));

export default RegionAddressesLayout;
